import { NumberField } from "@base-ui/react/number-field";
import type { TextFieldProps } from "@mui/material";
import { FormControl, FormHelperText, TextField } from "@mui/material";
import { useField } from "formik";
import {
  useCallback,
  useRef,
  type ChangeEvent,
  type ComponentPropsWithRef,
  type FocusEvent,
  type KeyboardEvent,
  type Ref,
} from "react";

import { useFormatMessage } from "@skilitics/localization";

import { useFormContext } from "../FormContext";
import { useFieldContext } from "../FormFieldContext";
import type { TestAttribute } from "../shared";
import { useCommitChange, useFieldLabelAndDescription } from "./helpers";

const warnings = {
  aboveMaximum: {
    en: "Must be equal to or less than {max}",
  },
  belowMinimum: {
    en: "Must be equal to or greater than {min}",
  },
  notInteger: {
    en: "The value {value} is not a whole number",
  },
  notFloat: {
    en: "The value {value} is not a number",
  },
};

type InputNumberProps = Omit<TextFieldProps, "variant" | "value" | "onChange"> &
  TestAttribute & {
    value?: number;
    /** Bounds on the stored value. Do not add `offset` — the control does, for the display and its warnings. */
    min?: number;
    /** Bounds on the stored value. Do not add `offset` — the control does, for the display and its warnings. */
    max?: number;
    emptyValue?: number;
    /** Commit an out-of-range entry at the bound it passed, rather than reverting to `emptyValue`. */
    clampToRange?: boolean;
    /** Added on display only — `1` shows a 0-based index from 1. Every number in or out is unshifted. */
    offset?: number;
    fractionDigits?: number;
    useMaxFractionDigits?: boolean;
    blurOnEnter?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>, value?: number) => void;
    onChangeCommitted?: (value?: number, event?: Event) => void;
    ref?: Ref<HTMLInputElement>;
  };

const shift = (value: number | undefined, by: number) => (value === undefined ? undefined : value + by);

const getWarningType = ({
  useFractionDigits,
  min,
  max,
  value,
}: Pick<InputNumberProps, "min" | "max" | "value"> & { useFractionDigits: boolean }):
  | keyof typeof warnings
  | undefined => {
  // an empty input is not a complaint about the number in it; `emptyValue` settles it on commit
  if (value === undefined) return undefined;
  if (!useFractionDigits && !Number.isInteger(value)) return "notInteger";
  if (useFractionDigits && !Number.isFinite(value)) return "notFloat";
  // zero is a number like any other here: a range may open above it or close below it
  if (min !== undefined && value < min) return "belowMinimum";
  if (max !== undefined && value > max) return "aboveMaximum";
};

export const InputNumber = (props: InputNumberProps) => {
  const {
    min,
    max,
    value,
    emptyValue,
    clampToRange,
    offset = 0,
    fractionDigits,
    useMaxFractionDigits,
    blurOnEnter = true,
    disabled,
    onBlur,
    onFocus,
    onKeyDown,
    onKeyUp,
    onChange,
    onChangeCommitted,
    dataTest,
    name,
    ref,
    slotProps,
    ...rest
  } = props;

  const formatMessage = useFormatMessage(warnings);

  const { hasValidationSchema } = useFormContext();

  const useFractionDigits = !!(useMaxFractionDigits || fractionDigits);

  // Everything below this line is in display units; the stored number reappears
  // only where a value is handed back to the caller.
  const displayValue = shift(value, offset);
  const displayMin = shift(min, offset);
  const displayMax = shift(max, offset);

  const warningType = getWarningType({ useFractionDigits, min: displayMin, max: displayMax, value: displayValue });

  // Mirror the formatting the field uses for display so typed values are normalised consistently
  // (e.g. flooring excess fraction digits). `useGrouping: false` keeps the text a plain number.
  const format: Intl.NumberFormatOptions = useFractionDigits
    ? {
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits ?? 100,
        roundingMode: "floor",
        useGrouping: false,
      }
    : { maximumFractionDigits: 0, useGrouping: false };

  // The latest value Base UI has reported, used to commit on blur. Base UI fires `onValueChange`
  // (including the blur-time formatting pass) but only fires `onValueCommitted` when something
  // actually commits — never on a plain focus-then-blur with no edit, which still needs to commit.
  const latestValueRef = useRef<number | undefined>(displayValue);

  // Out-of-range or empty input resets to `emptyValue` rather than clamping (we allow direct text
  // entry past min/max and decide the committed value ourselves). `clampToRange` opts into
  // committing at the bound instead.
  const commit = (committed: number | undefined, event?: Event) => {
    if (!disabled && clampToRange && committed !== undefined) {
      if (displayMin !== undefined && committed < displayMin) return onChangeCommitted?.(min, event);
      if (displayMax !== undefined && committed > displayMax) return onChangeCommitted?.(max, event);
    }

    const hasWarning =
      committed !== undefined &&
      getWarningType({ useFractionDigits, min: displayMin, max: displayMax, value: committed });
    const shouldReset = !disabled && emptyValue !== undefined && (committed === undefined || !!hasWarning);
    onChangeCommitted?.(shouldReset ? emptyValue : shift(committed, -offset), event);
  };

  return (
    <FormControl error={!disabled && !!warningType}>
      <NumberField.Root
        value={displayValue ?? null}
        min={displayMin}
        max={displayMax}
        allowOutOfRange
        disabled={disabled}
        locale="en"
        format={format}
        onValueChange={(newValue, details) => {
          latestValueRef.current = newValue ?? undefined;
          onChange?.(details.event as unknown as ChangeEvent<HTMLInputElement>, shift(newValue ?? undefined, -offset));
        }}
        onValueCommitted={(newValue, details) => {
          // Blur/clear commits are handled in the input's `onBlur` so a focus-then-blur with no edit
          // still commits; here we only handle the paths that commit without blurring (spinner
          // buttons, arrow keys, scrub, wheel).
          const stepReasons = ["increment-press", "decrement-press", "keyboard", "wheel", "scrub"];
          if (!stepReasons.includes(details.reason)) return;
          commit(newValue ?? undefined, details.event);
        }}
      >
        <NumberField.Input
          render={(inputProps) => {
            // Base UI types its render props as bare HTMLAttributes; the input also receives the
            // controlled value and event handlers it needs to manage parsing and formatting.
            const {
              ref: inputRef,
              value: inputValue,
              onChange: inputOnChange,
              onBlur: inputOnBlur,
              onFocus: inputOnFocus,
              onKeyDown: inputOnKeyDown,
              ...htmlInput
            } = inputProps as ComponentPropsWithRef<"input">;
            return (
              <TextField
                {...rest}
                ref={ref}
                inputRef={inputRef}
                color={warningType ? "error" : "primary"}
                disabled={disabled}
                value={inputValue}
                onChange={inputOnChange}
                onBlur={(event) => {
                  // Let Base UI run its blur formatting first (it updates `latestValueRef` via
                  // `onValueChange`), then commit whatever value the field settled on.
                  inputOnBlur?.(event as FocusEvent<HTMLInputElement>);
                  commit(latestValueRef.current, event.nativeEvent);
                  onBlur?.(event);
                }}
                onFocus={(event) => {
                  // Select all text on focus so the existing value is replaced when the user types.
                  event.target.select();
                  inputOnFocus?.(event as FocusEvent<HTMLInputElement>);
                  onFocus?.(event);
                }}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  inputOnKeyDown?.(event);
                  onKeyDown?.(event);
                }}
                onKeyUp={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (blurOnEnter && event.key === "Enter") {
                    (event.target as HTMLInputElement).blur();
                  }
                  onKeyUp?.(event);
                }}
                slotProps={{
                  ...slotProps,
                  htmlInput: {
                    ...htmlInput,
                    ...slotProps?.htmlInput,
                    inputMode: useFractionDigits ? "decimal" : "numeric",
                    min: displayMin,
                    max: displayMax,
                    "data-test": `${dataTest ?? name}-input`,
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            );
          }}
        />
      </NumberField.Root>

      {!disabled && warningType && !hasValidationSchema && (
        <FormHelperText>
          {formatMessage(warningType, { max: displayMax, min: displayMin, value: displayValue })}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export type FieldInputNumberProps = Omit<InputNumberProps, "helperText">;

export const FieldInputNumber = (props: FieldInputNumberProps) => {
  const { min, onChangeCommitted } = props;
  // Distinguish explicitly-passed emptyValue={undefined} from omitted emptyValue.
  // JS destructuring defaults treat both as undefined, so we check the props object directly.
  const emptyValue = "emptyValue" in props ? props.emptyValue : (min ?? 0);

  const commitChange = useCommitChange();

  const { controlId, dataTest, name } = useFieldContext();

  const [{ value }, _, { setValue }] = useField<number>(name);
  const [label, helperText] = useFieldLabelAndDescription(name);

  // Live updates as the user types or steps; the committed value is written on blur / step release.
  const handleChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement> | undefined, newValue?: number) => {
      void setValue(newValue as number);
    },
    [setValue],
  );

  const handleChangeCommitted = useCallback(
    (committedValue?: number, event?: Event) => {
      setValue(committedValue as number)
        .then(() => {
          onChangeCommitted?.(committedValue, event);
          commitChange();
        })
        .catch(console.warn);
    },
    [commitChange, onChangeCommitted, setValue],
  );

  // Display emptyValue as fallback while formik value is undefined (e.g. on mount).
  const displayValue = value ?? emptyValue;

  return (
    <InputNumber
      {...props}
      name={name}
      value={displayValue}
      emptyValue={emptyValue}
      id={controlId}
      onChange={handleChange}
      label={props.label ?? label}
      helperText={helperText}
      onChangeCommitted={handleChangeCommitted}
      dataTest={dataTest}
    />
  );
};
